import { Helmet } from 'react-helmet';

export const SITE_URL = 'https://www.keepmyasset.com';
export const SITE_NAME = 'KeepMyAsset';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/Logo.png`;

const DEFAULT_DESCRIPTION =
  'KeepMyAsset helps families secure bank accounts, PPF, EPF, insurance, property, and more—assign nominees and ensure loved ones are informed when it matters most.';

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
  description: DEFAULT_DESCRIPTION,
  email: 'team.keepmyasset@gmail.com',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: 'team.keepmyasset@gmail.com',
    availableLanguage: ['English', 'Hindi'],
  },
  sameAs: [
    'https://twitter.com/keepmyasset/',
    'https://www.linkedin.com/in/keepmyassets-keepmyassets-904a86372/',
    'https://instagram.com/keepmyassets/',
  ],
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
  publisher: { '@type': 'Organization', name: SITE_NAME },
};

export function buildFaqSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: a,
      },
    })),
  };
}

export function buildBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map(({ name, path }, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name,
      item: `${SITE_URL}${path}`,
    })),
  };
}

function formatTitle(title) {
  if (!title) return SITE_NAME;
  return title.includes('KeepMyAsset') ? title : `${title} | ${SITE_NAME}`;
}

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  noindex = false,
  keywords,
  jsonLd,
}) {
  const canonical = path === '/' ? SITE_URL : `${SITE_URL}${path}`;
  const pageTitle = formatTitle(title);

  const schemas = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <html lang="en" />
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}
      <link rel="canonical" href={canonical} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schemas.map((schema) => (
        <script key={schema['@type']} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
